import { doc, runTransaction } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, collectionRef, studentDoc } from '../firebase/config';
import {
  COLLECTIONS,
  PAYMENT_LIMITS, STUDENT_STATUS, USER_ROLE,
} from '../utils/constants';
import { generateUserCode } from '../utils/stringUtils';
import { withRetry } from '../utils/firestoreRetry';
import { normalizeEmail, normalizeEmailOrPhone } from '../utils/emailUtils';
import { normalizePhoneTR } from '../utils/phoneUtils';
import { buildNextFeeHistory } from '../utils/paymentPeriods';
import {
  extractIdentityValues,
  syncIdentitiesInTx,
} from './identityUniqueness';

function resolveStudentContact(formData) {
  const rawEmail = String(formData.email || '').trim();
  const rawPhone = String(formData.phone || formData.emailOrPhone || '').trim();

  const email = rawEmail.includes('@') ? normalizeEmail(rawEmail) : '';
  const phone = normalizePhoneTR(rawPhone) || '';

  // Geriye dönük: tek alanda mail veya telefon
  if (!email && !phone && formData.emailOrPhone) {
    const combined = normalizeEmailOrPhone(formData.emailOrPhone);
    if (!combined) return null;
    if (combined.includes('@')) {
      return { emailOrPhone: combined, phoneNumber: undefined };
    }
    return { emailOrPhone: combined, phoneNumber: combined };
  }

  if (!email && !phone) return null;

  return {
    emailOrPhone: email || phone,
    phoneNumber: phone || undefined,
  };
}

export async function addStudentByInstructor(instructor, formData) {
  const contact = resolveStudentContact(formData);
  if (!contact) {
    throw new Error('INVALID_CONTACT');
  }

  const studentsCol = collectionRef(COLLECTIONS.STUDENTS);
  const studentRef = doc(studentsCol);
  const profileData = {
    name: formData.name,
    emailOrPhone: contact.emailOrPhone,
    role: USER_ROLE.STUDENT,
    userCode: generateUserCode(),
    instructorId: instructor.id,
    instructorEmail: instructor.emailOrPhone,
    age: formData.age,
    parent: formData.parent || '-',
    monthlyFee: 0,
    status: STUDENT_STATUS.ACTIVE,
    createdAt: Date.now(),
    ...(contact.phoneNumber ? { phoneNumber: contact.phoneNumber } : {}),
  };

  await runTransaction(db, async (tx) => {
    await syncIdentitiesInTx(tx, studentRef.id, {
      claim: extractIdentityValues(profileData),
    });
    tx.set(studentRef, profileData);
  });

  await activateStudentAccount(studentRef.id);
  return studentRef.id;
}

/**
 * Öğrencinin giriş hesabını (Auth) belge ID'siyle hemen oluşturur.
 * Başarısız olsa bile öğrenci ilk girişte sunucu tarafında yine aktifleşir.
 */
export async function activateStudentAccount(studentId) {
  try {
    await httpsCallable(functions, 'activateStudentAccount')({ studentId });
    return true;
  } catch (err) {
    console.warn('Öğrenci hesabı aktifleştirilemedi (girişte tekrar denenecek):', err);
    return false;
  }
}

/**
 * Öğrenciyi kalıcı siler (sunucuda): belge, programlar, dereceler, e-posta/telefon rezervasyonu
 * ve giriş hesabı; ödemeler arşive taşınır. Aynı bilgilerle yeniden kayıt olunabilir.
 */
export async function deleteStudentPermanently(studentId) {
  if (!studentId) throw new Error('Öğrenci bulunamadı.');
  await httpsCallable(functions, 'deleteStudent')({ studentId });
}

/**
 * Öğrencinin aylık ücreti (aidat). Aylara kendiliğinden yazılmaz: kaydı olmayan bir ay
 * "Ödendi" yapılınca o ay için, "12 ay" ile seçili yılın ayları için kullanılır. 0 = tanımlanmadı.
 * monthlyFeeHistory, tutar alanı olmayan eski ay kayıtlarının o dönemki ücretle gösterilmesi içindir.
 */
export async function setStudentMonthlyFee(studentId, monthlyFee) {
  if (!studentId) throw new Error('Öğrenci bulunamadı.');
  const value = Number(monthlyFee);
  if (!Number.isFinite(value) || value < 0 || value > PAYMENT_LIMITS.MAX_AMOUNT) {
    throw new Error('Geçersiz aylık ücret.');
  }
  const fee = Math.round(value * 100) / 100;
  const ref = studentDoc(studentId);
  await withRetry(() =>
    runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('Öğrenci bulunamadı.');
      tx.update(ref, {
        monthlyFee: fee,
        monthlyFeeHistory: buildNextFeeHistory(snap.data(), fee),
      });
    })
  );
}
